import { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Eye, EyeSlash, SpinnerGap, CheckCircle, WarningCircle } from '@phosphor-icons/react';

const API = `${""}/api`;

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = useMemo(() => searchParams.get('token') || '', [searchParams]);

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!token) setError('Link inválido ou em falta.');
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (password.length < 6) {
      setError('A palavra-passe tem de ter pelo menos 6 caracteres.');
      return;
    }
    if (password !== confirm) {
      setError('As palavras-passe não coincidem.');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, new_password: password });
      setSuccess(true);
      setTimeout(() => navigate('/login', { replace: true }), 2500);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Não foi possível repor a palavra-passe.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-8 bg-[#F3F4F6]" style={{ fontFamily: "'Manrope', sans-serif" }}>
      <div className="w-full max-w-[440px] bg-white rounded-xl shadow-lg p-8" data-testid="reset-password-card">
        <div className="mb-8">
          <img src="/logo-ihtp-dark.svg" alt="IHTP" className="h-7 w-auto mb-3" data-testid="reset-brand-logo" />
          <p className="text-[#6B7280] text-sm">Definir nova palavra-passe</p>
        </div>

        {success ? (
          <div data-testid="reset-password-success" className="p-5 rounded-lg bg-green-50 border border-green-200 text-green-800 flex items-start gap-3">
            <CheckCircle size={22} weight="fill" className="mt-0.5 shrink-0" />
            <div>
              <p className="font-semibold text-sm">Palavra-passe actualizada</p>
              <p className="text-xs mt-1">Vai ser redirecionado para o login…</p>
            </div>
          </div>
        ) : (
          <>
            {error && (
              <div data-testid="reset-password-error" className="mb-4 p-3 rounded-md bg-red-50 border border-red-200 text-[#FF2B2B] text-sm flex items-start gap-2">
                <WarningCircle size={18} className="mt-0.5 shrink-0" />
                <span>{error}</span>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label className="text-xs uppercase tracking-[0.2em] font-medium text-[#6B7280] mb-1.5 block">Nova palavra-passe</label>
                <div className="relative">
                  <input data-testid="reset-password-new" type={showPw ? 'text' : 'password'} value={password}
                    onChange={(e) => setPassword(e.target.value)} required minLength={6}
                    className="w-full px-3 py-2.5 pr-10 border border-[#E5E7EB] rounded-md text-sm text-[#111827] focus:outline-none focus:ring-2 focus:ring-[#0F1829] transition-all" />
                  <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#6B7280]" aria-label="toggle password visibility">
                    {showPw ? <Eye size={18} /> : <EyeSlash size={18} />}
                  </button>
                </div>
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.2em] font-medium text-[#6B7280] mb-1.5 block">Confirmar palavra-passe</label>
                <input data-testid="reset-password-confirm" type={showPw ? 'text' : 'password'} value={confirm}
                  onChange={(e) => setConfirm(e.target.value)} required minLength={6}
                  className="w-full px-3 py-2.5 border border-[#E5E7EB] rounded-md text-sm text-[#111827] focus:outline-none focus:ring-2 focus:ring-[#0F1829] transition-all" />
              </div>
              <button type="submit" disabled={loading || !token} data-testid="reset-password-submit"
                className="w-full py-2.5 bg-[#0F1829] text-white text-sm font-medium rounded-md hover:bg-[#002480] active:scale-[0.98] transition-all disabled:opacity-50 flex items-center justify-center gap-2">
                {loading ? <SpinnerGap size={18} className="animate-spin" /> : null}
                {loading ? 'A guardar…' : 'Repor palavra-passe'}
              </button>
            </form>

            <div className="mt-6 text-center">
              <Link to="/login" className="text-xs text-[#6B7280] hover:text-[#0F1829] transition-colors" data-testid="reset-password-back-login">
                Voltar ao login
              </Link>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
